/** v1's typed-delete confirmation (js/typed-delete.js), verbatim behavior:
 * every destructive action runs through a modal that requires typing
 * "delete" before the red button unlocks; Enter submits once unlocked. */
import { useState, type ReactNode } from "react";

export const deleteUnlocked = (typed: string): boolean => typed.trim().toLowerCase() === "delete";

/** v1's global confirmDelete(msg, onYes) as a hook: call `ask(msg, onYes)` from
 * any delete button, render `{modal}` once at the bottom of the component. The
 * action only runs after the user types "delete" and confirms. */
export function useConfirmDelete(): { ask: (message: string, onYes: () => void) => void; modal: ReactNode } {
  const [pending, setPending] = useState<{ message: string; onYes: () => void } | null>(null);
  const ask = (message: string, onYes: () => void) => setPending({ message, onYes });
  const modal = pending ? (
    <ConfirmDelete
      message={pending.message}
      onCancel={() => setPending(null)}
      onConfirm={() => { const fn = pending.onYes; setPending(null); fn(); }}
    />
  ) : null;
  return { ask, modal };
}

export function ConfirmDelete({
  message,
  onCancel,
  onConfirm,
}: {
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const ok = deleteUnlocked(typed);
  return (
    <div className="calmodalwrap" onClick={onCancel}>
      <div className="calmodal" onClick={(e) => e.stopPropagation()}>
        <div className="calmodalhead">
          <h2 className="deltitle">Confirm delete</h2>
          <button className="taskdel" onClick={onCancel}>×</button>
        </div>
        <div className="calmodalbody">
          <p className="delmsg">{message}</p>
          <div className="arvfield">
            <label className="lbl">
              Type <b className="delword">delete</b> to confirm
            </label>
            <input
              className="moneyinput"
              autoFocus
              autoComplete="off"
              placeholder="delete"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && ok) onConfirm();
                if (e.key === "Escape") onCancel();
              }}
            />
          </div>
        </div>
        <div className="calmodalfoot">
          <button className="out" onClick={onCancel}>Cancel</button>
          <button className="btn danger" disabled={!ok} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}
